// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2022 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { createAction, ThunkAction } from 'utils/redux';

import { getCore, Storage } from 'cvat-core-wrapper';

const core = getCore();

export enum ExportActionTypes {
    OPEN_EXPORT_DATASET_MODAL = 'OPEN_EXPORT_DATASET_MODAL',
    CLOSE_EXPORT_DATASET_MODAL = 'CLOSE_EXPORT_DATASET_MODAL',
    EXPORT_DATASET = 'EXPORT_DATASET',
    EXPORT_DATASET_SUCCESS = 'EXPORT_DATASET_SUCCESS',
    EXPORT_DATASET_FAILED = 'EXPORT_DATASET_FAILED',
    OPEN_EXPORT_BACKUP_MODAL = 'OPEN_EXPORT_BACKUP_MODAL',
    CLOSE_EXPORT_BACKUP_MODAL = 'CLOSE_EXPORT_BACKUP_MODAL',
    EXPORT_BACKUP = 'EXPORT_BACKUP',
    EXPORT_BACKUP_SUCCESS = 'EXPORT_BACKUP_SUCCESS',
    EXPORT_BACKUP_FAILED = 'EXPORT_BACKUP_FAILED',
    OPEN_EXPORT_BLENDER_MODAL = 'OPEN_EXPORT_BLENDER_MODAL',
    CLOSE_EXPORT_BLENDER_MODAL = 'CLOSE_EXPORT_BLENDER_MODAL',
    EXPORT_BLENDER = 'EXPORT_BLENDER',
    EXPORT_BLENDER_SUCCESS = 'EXPORT_BLENDER_SUCCESS',
    EXPORT_BLENDER_FAILED = 'EXPORT_BLENDER_FAILED',
}

export const exportActions = {
    openExportDatasetModal: (instance: any) => createAction(ExportActionTypes.OPEN_EXPORT_DATASET_MODAL, { instance }),
    closeExportDatasetModal: (instance: any) => createAction(
        ExportActionTypes.CLOSE_EXPORT_DATASET_MODAL, { instance },
    ),
    exportDataset: (instance: any, format: string) => createAction(
        ExportActionTypes.EXPORT_DATASET, { instance, format },
    ),
    exportDatasetSuccess: (
        instance: any,
        instanceType: 'project' | 'task' | 'reference' | 'job' | 'reference_job',
        format: string,
        isLocal: boolean,
        resource: 'Dataset' | 'Annotations' | 'SingleFile',
    ) => createAction(ExportActionTypes.EXPORT_DATASET_SUCCESS, {
        instance,
        instanceType,
        format,
        isLocal,
        resource,
    }),
    exportDatasetFailed: (
        instance: any,
        instanceType: 'project' | 'task' | 'reference' | 'job' | 'reference_job',
        format: string,
        error: any,
    ) => createAction(ExportActionTypes.EXPORT_DATASET_FAILED, {
        instance,
        instanceType,
        format,
        error,
    }),
    openExportBackupModal: (instance: any) => createAction(ExportActionTypes.OPEN_EXPORT_BACKUP_MODAL, { instance }),
    closeExportBackupModal: (instance: any) => createAction(ExportActionTypes.CLOSE_EXPORT_BACKUP_MODAL, { instance }),
    exportBackup: (instance: any) => createAction(ExportActionTypes.EXPORT_BACKUP, { instance }),
    exportBackupSuccess: (instance: any, instanceType: 'task' | 'project' | 'reference', isLocal: boolean) => createAction(ExportActionTypes.EXPORT_BACKUP_SUCCESS, { instance, instanceType, isLocal }),
    exportBackupFailed: (instance: any, instanceType: 'task' | 'project' | 'reference', error: any) => createAction(ExportActionTypes.EXPORT_BACKUP_FAILED, { instance, instanceType, error }),
    openExportBlenderModal: (instance: any) => createAction(ExportActionTypes.OPEN_EXPORT_BLENDER_MODAL, { instance }),
    closeExportBlenderModal: (instance: any) => createAction(
        ExportActionTypes.CLOSE_EXPORT_BLENDER_MODAL, { instance },
    ),
    exportBlender: (instance: any) => createAction(ExportActionTypes.EXPORT_BLENDER, { instance }),
    exportBlenderSuccess: (instance: any, instanceType: 'task' | 'project' | 'reference', isLocal: boolean) => createAction(ExportActionTypes.EXPORT_BLENDER_SUCCESS, { instance, instanceType, isLocal }),
    exportBlenderFailed: (instance: any, instanceType: 'task' | 'project' | 'reference', error: any) => createAction(ExportActionTypes.EXPORT_BLENDER_FAILED, { instance, instanceType, error }),
};

export const exportDatasetAsync =
    (
        instance: any,
        format: string,
        saveImages: boolean,
        useDefaultSettings: boolean,
        targetStorage: Storage,
        name?: string,
        floorsAsLevels = false,
        autoLP = false,
        forAddon = false,
        singleFile = false,
        sceneMode = 'VR',
        sceneName?: string,
        frames?: string,
    ): ThunkAction => async (dispatch) => {
        dispatch(exportActions.exportDataset(instance, format));

        let instanceType: 'project' | 'task' | 'reference' | 'job' | 'reference_job';
        if (instance instanceof core.classes.Project) {
            instanceType = 'project';
        } else if (instance instanceof core.classes.Task) {
            instanceType = 'task';
        } else if (instance instanceof core.classes.Reference) {
            instanceType = 'reference';
        } else if (instance instanceof core.classes.ReferenceJob) {
            instanceType = 'reference_job';
        } else {
            instanceType = 'job';
        }

        try {
            const result = await instance.annotations.exportDataset(
                format,
                saveImages,
                useDefaultSettings,
                targetStorage,
                name,
                floorsAsLevels,
                autoLP,
                forAddon,
                singleFile,
                sceneMode,
                sceneName,
                frames,
            );
            if (result) {
                const downloadAnchor = window.document.getElementById('downloadAnchor') as HTMLAnchorElement;
                downloadAnchor.href = result;
                downloadAnchor.click();
            }
            let resource = 'Annotations';
            if (saveImages) {
                resource = 'Dataset';
            } else if (singleFile) {
                resource = 'SingleFile';
            }
            dispatch(exportActions.exportDatasetSuccess(instance, instanceType, format, !!result, resource));
        } catch (error) {
            dispatch(exportActions.exportDatasetFailed(instance, instanceType, format, error));
        }
    };

export const exportBackupAsync =
    (instance: any, targetStorage: Storage, useDefaultSetting: boolean, fileName?: string):
    ThunkAction => async (dispatch) => {
        dispatch(exportActions.exportBackup(instance));
        let instanceType: 'project' | 'task' | 'reference';
        if (instance instanceof core.classes.Project) {
            instanceType = 'project';
        } else if (instance instanceof core.classes.Task) {
            instanceType = 'task';
        } else if (instance instanceof core.classes.Reference) {
            instanceType = 'reference';
        } else {
            instanceType = 'task';
        }

        try {
            const result = await instance.backup(targetStorage, useDefaultSetting, fileName);
            if (result) {
                const downloadAnchor = window.document.getElementById('downloadAnchor') as HTMLAnchorElement;
                downloadAnchor.href = result;
                downloadAnchor.click();
            }
            dispatch(exportActions.exportBackupSuccess(instance, instanceType, !!result));
        } catch (error) {
            dispatch(exportActions.exportBackupFailed(instance, instanceType, error as Error));
        }
    };
